import { useMemo, useState } from "react";

const SIZE = 460;
const CENTER = SIZE / 2;
const MAX_R = 168;
const RINGS = [2, 4, 6, 8, 10];

function polar(angle, r) {
  return {
    x: CENTER + r * Math.cos(angle),
    y: CENTER + r * Math.sin(angle),
  };
}

export default function ReportRadarChart({ axes }) {
  const [hovered, setHovered] = useState(null);
  const [view, setView] = useState("radar");
  const n = axes.length;
  const angleStep = (Math.PI * 2) / n;
  const startAngle = -Math.PI / 2;

  const points = useMemo(
    () =>
      axes.map((axis, i) => {
        const angle = startAngle + i * angleStep;
        const r = (axis.value / 10) * MAX_R;
        const point = polar(angle, r);
        const labelPoint = polar(angle, MAX_R + 38);
        const valuePoint = polar(angle, MAX_R + 16);
        return { ...axis, angle, point, labelPoint, valuePoint };
      }),
    [axes],
  );

  const polygonPath = points.map((p) => `${p.point.x},${p.point.y}`).join(" ");

  return (
    <div className="pr-radar-wrap">
      {view === "radar" ? (
        <svg viewBox="-80 -40 620 540" className="pr-radar-svg" role="img" aria-label="Player capability radar chart">
          {RINGS.map((ring) => {
            const r = (ring / 10) * MAX_R;
            const ringPoints = Array.from({ length: n }, (_, i) => {
              const angle = startAngle + i * angleStep;
              return polar(angle, r);
            })
              .map((p) => `${p.x},${p.y}`)
              .join(" ");
            return (
              <polygon
                key={ring}
                points={ringPoints}
                className={ring === 10 ? "pr-ring pr-ring--outer" : "pr-ring"}
              />
            );
          })}

          {points.map((p, i) => (
            <line
              key={`spoke-${i}`}
              x1={CENTER}
              y1={CENTER}
              x2={polar(p.angle, MAX_R).x}
              y2={polar(p.angle, MAX_R).y}
              className="pr-spoke"
            />
          ))}

          {RINGS.map((ring) => (
            <text
              key={`ring-label-${ring}`}
              x={CENTER + 6}
              y={CENTER - (ring / 10) * MAX_R}
              className="pr-ring-label"
            >
              {ring}
            </text>
          ))}

          <polygon points={polygonPath} className="pr-data-fill" />
          <polygon points={polygonPath} className="pr-data-outline" />

          {points.map((p, i) => (
            <circle
              key={`pt-${i}`}
              cx={p.point.x}
              cy={p.point.y}
              r={hovered === i ? 7 : 5}
              className="pr-vertex"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}

          {points.map((p, i) => {
            const isRight = p.labelPoint.x > CENTER + 4;
            const isLeft = p.labelPoint.x < CENTER - 4;
            const isBottom = p.labelPoint.y > CENTER + 20;
            const anchor = isRight ? "start" : isLeft ? "end" : "middle";
            return (
              <g key={`label-${i}`}>
                <text x={p.labelPoint.x} y={p.labelPoint.y + (isBottom ? 14 : -8)} textAnchor={anchor} className="pr-axis-label">
                  {p.label}
                </text>
                <text x={p.valuePoint.x} y={p.valuePoint.y + (isBottom ? -8 : 18)} textAnchor={anchor} className="pr-axis-value">
                  {p.value.toFixed(1)}
                </text>
              </g>
            );
          })}
        </svg>
      ) : (
        <div className="pr-bars" role="img" aria-label="Player capability bar chart">
          {axes.map((axis) => (
            <div className="pr-bar-row" key={axis.key}>
              <div className="pr-bar-header">
                <span>{axis.label}</span>
                <strong>{axis.value.toFixed(1)}</strong>
              </div>
              <div className="pr-bar-track">
                <div className="pr-bar-fill" style={{ width: `${axis.value * 10}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pr-view-toggle" role="group" aria-label="Chart view">
        <button
          type="button"
          className={view === "radar" ? "is-active" : ""}
          onClick={() => setView("radar")}
          aria-pressed={view === "radar"}
          aria-label="Show radar chart"
          title="Radar chart"
        >
          <span className="pr-view-icon pr-view-icon--radar" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={view === "bars" ? "is-active" : ""}
          onClick={() => setView("bars")}
          aria-pressed={view === "bars"}
          aria-label="Show bar chart"
          title="Bar chart"
        >
          <span className="pr-view-icon pr-view-icon--bars" aria-hidden="true" />
        </button>
      </div>

      {hovered !== null && (
        <div className="pr-tooltip">
          <strong>{points[hovered].label}</strong>
          <span>{points[hovered].value.toFixed(1)} / 10</span>
          <span className="pr-tooltip-detail">{points[hovered].detail}</span>
        </div>
      )}
    </div>
  );
}
